/**
 * HTTP transport core of AdCreativeApiClient (FR0-I1).
 *
 * Kept free of import.meta / firebase imports so it stays unit-testable under
 * jest (CJS). Provides:
 * - in-flight dedupe: identical concurrent calls share one network request;
 * - navigation-scoped aborts: a new request with the same abortKey supersedes
 *   (aborts) the previous one;
 * - a hard per-request timeout;
 * - auth token memoization with a single forced-refresh retry on 401.
 */

export interface AdCreativeTransportRequest {
  path: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Browser-owned multipart body. Content-Type must be generated by fetch. */
  formData?: FormData;
  baseUrl: string;
  /** Requests sharing an abortKey supersede each other (newer aborts older). */
  abortKey?: string;
  /** Passed through to onHttpError (billing-block attribution). */
  projectId?: string;
  /** Narrow per-request override for bounded uploads/long-running transfers. */
  timeoutMs?: number;
}

export interface AdCreativeTransportDeps {
  /** Resolve the bearer token; forceRefresh bypasses provider-side caches. */
  fetchToken: (forceRefresh: boolean) => Promise<string | null>;
  /**
   * Map a non-ok HTTP payload to a client response. Return undefined to fall
   * through to the generic `{ success:false, error }` shape.
   */
  onHttpError?: (status: number, data: Record<string, unknown>, request: AdCreativeTransportRequest) => unknown | undefined;
  /** Appended to the unreachable-API message (local-dev hint). */
  unreachableHint?: (baseUrl: string) => string;
  timeoutMs?: number;
}

export interface AdCreativeBinaryTransportResponse {
  success: boolean;
  blob?: Blob;
  mimeType?: 'audio/wav' | 'audio/mp4';
  byteSize?: number;
  sha256?: string;
  etag?: string;
  error?: string;
  status?: number;
  code?: string;
  aborted?: boolean;
}

const DEFAULT_TIMEOUT_MS = 30_000;
const MAX_ERROR_MESSAGE_LENGTH = 1_000;
const MAX_ERROR_DETAIL_DEPTH = 4;
const MAX_ERROR_DETAIL_KEYS = 50;
const MAX_ERROR_DETAIL_ARRAY_LENGTH = 50;
const MAX_APPROVED_AUDIO_PLAYBACK_BYTES = 30 * 1024 * 1024;

interface SanitizedHttpError {
  success: false;
  error: string;
  status: number;
  code?: string;
  details?: Record<string, unknown>;
}

export class AdCreativeRequestTransport {
  private readonly inFlight = new Map<string, Promise<unknown>>();
  private readonly abortByKey = new Map<string, AbortController>();
  private cachedToken: string | null = null;
  private multipartSequence = 0;

  constructor(private readonly deps: AdCreativeTransportDeps) {}

  request<T>(request: AdCreativeTransportRequest): Promise<T> {
    const dedupeBody = request.formData
      ? `multipart:${this.multipartSequence += 1}`
      : request.body ? JSON.stringify(request.body) : '';
    const dedupeKey = `${request.method}:${request.baseUrl}${request.path}:${dedupeBody}`;
    const existing = this.inFlight.get(dedupeKey);
    if (existing) return existing as Promise<T>;

    const promise = this.execute<T>(request, false).finally(() => {
      this.inFlight.delete(dedupeKey);
    });
    this.inFlight.set(dedupeKey, promise);
    return promise;
  }

  requestBinary(
    request: Omit<AdCreativeTransportRequest, 'body' | 'formData'> & { method: 'GET' },
  ): Promise<AdCreativeBinaryTransportResponse> {
    const dedupeKey = `binary:${request.method}:${request.baseUrl}${request.path}`;
    const existing = this.inFlight.get(dedupeKey);
    if (existing) return existing as Promise<AdCreativeBinaryTransportResponse>;
    const promise = this.executeBinary(request, false).finally(() => {
      this.inFlight.delete(dedupeKey);
    });
    this.inFlight.set(dedupeKey, promise);
    return promise;
  }

  private async resolveToken(forceRefresh: boolean): Promise<string | null> {
    if (!forceRefresh && this.cachedToken) return this.cachedToken;
    this.cachedToken = (await this.deps.fetchToken(forceRefresh)) ?? null;
    return this.cachedToken;
  }

  private async execute<T>(request: AdCreativeTransportRequest, isAuthRetry: boolean): Promise<T> {
    const controller = new AbortController();
    if (request.abortKey) {
      this.abortByKey.get(request.abortKey)?.abort();
      this.abortByKey.set(request.abortKey, controller);
    }
    const timeoutMs = request.timeoutMs ?? this.deps.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    if (!Number.isSafeInteger(timeoutMs) || timeoutMs <= 0 || timeoutMs > 10 * 60_000) {
      throw new Error('Ad Creative request timeout must be between 1 ms and 10 minutes');
    }
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const token = await this.resolveToken(isAuthRetry);
      if (!token) {
        return { success: false, error: 'User not authenticated' } as T;
      }
      const response = await fetch(`${request.baseUrl}${request.path}`, {
        method: request.method,
        headers: request.formData
          ? { Authorization: `Bearer ${token}` }
          : {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        body: request.formData
          ?? (request.body === undefined ? undefined : JSON.stringify(request.body)),
        signal: controller.signal,
      });
      if (response.status === 401 && !isAuthRetry) {
        this.cachedToken = null;
        return this.execute<T>(request, true);
      }
      const data = await response.json();
      if (!response.ok) {
        const payload = isRecord(data) ? data : {};
        const sanitized = sanitizeHttpError(response.status, payload);
        const mapped = this.deps.onHttpError?.(response.status, payload, request);
        if (mapped !== undefined) return enrichMappedHttpError(mapped, sanitized) as T;
        return sanitized as T;
      }
      return data as T;
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') {
        return { success: false, error: 'Request superseded or timed out', aborted: true } as T;
      }
      const message = typeof (error as { message?: unknown })?.message === 'string'
        ? (error as { message: string }).message
        : 'Network error';
      const hint = this.deps.unreachableHint?.(request.baseUrl) ?? '';
      return {
        success: false,
        error: `Ad Creative API is unreachable at ${request.baseUrl}. ${message}.${hint}`,
      } as T;
    } finally {
      clearTimeout(timeout);
      if (request.abortKey && this.abortByKey.get(request.abortKey) === controller) {
        this.abortByKey.delete(request.abortKey);
      }
    }
  }

  private async executeBinary(
    request: Omit<AdCreativeTransportRequest, 'body' | 'formData'> & { method: 'GET' },
    isAuthRetry: boolean,
  ): Promise<AdCreativeBinaryTransportResponse> {
    const controller = new AbortController();
    if (request.abortKey) {
      this.abortByKey.get(request.abortKey)?.abort();
      this.abortByKey.set(request.abortKey, controller);
    }
    const timeoutMs = request.timeoutMs ?? this.deps.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    if (!Number.isSafeInteger(timeoutMs) || timeoutMs <= 0 || timeoutMs > 10 * 60_000) {
      throw new Error('Ad Creative request timeout must be between 1 ms and 10 minutes');
    }
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const token = await this.resolveToken(isAuthRetry);
      if (!token) return { success: false, error: 'User not authenticated' };
      const response = await fetch(`${request.baseUrl}${request.path}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (response.status === 401 && !isAuthRetry) {
        this.cachedToken = null;
        return this.executeBinary(request, true);
      }
      if (!response.ok) {
        let payload: Record<string, unknown> = {};
        try {
          const value = await response.json();
          if (isRecord(value)) payload = value;
        } catch {
          // Never surface arbitrary binary/text bodies from an upstream error.
        }
        return sanitizeHttpError(response.status, payload);
      }
      const mimeType = normalizedApprovedAudioMime(response.headers.get('content-type'));
      const byteSize = normalizedApprovedAudioByteSize(response.headers.get('content-length'));
      const { etag, sha256 } = normalizedApprovedAudioEtag(response.headers.get('etag'));
      if (!mimeType || !byteSize || !etag || !sha256) {
        return invalidApprovedAudioPlaybackResponse();
      }
      const bytes = await response.arrayBuffer();
      if (bytes.byteLength !== byteSize || bytes.byteLength > MAX_APPROVED_AUDIO_PLAYBACK_BYTES) {
        return invalidApprovedAudioPlaybackResponse();
      }
      return {
        success: true,
        blob: new Blob([bytes], { type: mimeType }),
        mimeType,
        byteSize,
        sha256,
        etag,
      };
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') {
        return { success: false, error: 'Request superseded or timed out', aborted: true };
      }
      const message = typeof (error as { message?: unknown })?.message === 'string'
        ? (error as { message: string }).message
        : 'Network error';
      const hint = this.deps.unreachableHint?.(request.baseUrl) ?? '';
      return {
        success: false,
        error: `Ad Creative API is unreachable at ${request.baseUrl}. ${message}.${hint}`,
      };
    } finally {
      clearTimeout(timeout);
      if (request.abortKey && this.abortByKey.get(request.abortKey) === controller) {
        this.abortByKey.delete(request.abortKey);
      }
    }
  }
}

function normalizedApprovedAudioMime(value: string | null): 'audio/wav' | 'audio/mp4' | undefined {
  const mime = value?.split(';', 1)[0]?.trim().toLowerCase();
  return mime === 'audio/wav' || mime === 'audio/mp4' ? mime : undefined;
}

function normalizedApprovedAudioByteSize(value: string | null): number | undefined {
  if (!value || !/^[1-9][0-9]{0,8}$/.test(value)) return undefined;
  const size = Number(value);
  return Number.isSafeInteger(size) && size <= MAX_APPROVED_AUDIO_PLAYBACK_BYTES
    ? size
    : undefined;
}

function normalizedApprovedAudioEtag(value: string | null): {
  etag?: string;
  sha256?: string;
} {
  const match = value?.match(/^\"([a-f0-9]{64})\"$/);
  return match ? { etag: value ?? undefined, sha256: match[1] } : {};
}

function invalidApprovedAudioPlaybackResponse(): AdCreativeBinaryTransportResponse {
  return {
    success: false,
    error: 'The approved-audio playback response failed integrity checks.',
    status: 502,
    code: 'approved_audio_playback_response_invalid',
  };
}

function sanitizeHttpError(status: number, payload: Record<string, unknown>): SanitizedHttpError {
  const code = sanitizeErrorCode(payload.code);
  const details = sanitizeErrorDetails(payload.details);
  return {
    success: false,
    error: sanitizeErrorText(payload.error, `API error: ${status}`),
    status,
    ...(code ? { code } : {}),
    ...(details ? { details } : {}),
  };
}

function enrichMappedHttpError(mapped: unknown, sanitized: SanitizedHttpError): unknown {
  if (!isRecord(mapped)) return mapped;
  return {
    ...mapped,
    ...(!Object.prototype.hasOwnProperty.call(mapped, 'status') ? { status: sanitized.status } : {}),
    ...(!Object.prototype.hasOwnProperty.call(mapped, 'code') && sanitized.code
      ? { code: sanitized.code }
      : {}),
    ...(!Object.prototype.hasOwnProperty.call(mapped, 'details') && sanitized.details
      ? { details: sanitized.details }
      : {}),
  };
}

function sanitizeErrorCode(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return /^[a-z0-9][a-z0-9_.:-]{0,127}$/iu.test(normalized) ? normalized : undefined;
}

function sanitizeErrorDetails(value: unknown): Record<string, unknown> | undefined {
  if (!isRecord(value)) return undefined;
  return sanitizeDetailRecord(value, 0);
}

function sanitizeDetailRecord(value: Record<string, unknown>, depth: number): Record<string, unknown> {
  if (depth >= MAX_ERROR_DETAIL_DEPTH) return {};
  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value).slice(0, MAX_ERROR_DETAIL_KEYS)) {
    if (isSensitiveErrorKey(key)) {
      result[key] = '[redacted]';
      continue;
    }
    const sanitized = sanitizeDetailValue(item, depth + 1);
    if (sanitized !== undefined) result[key] = sanitized;
  }
  return result;
}

function sanitizeDetailValue(value: unknown, depth: number): unknown {
  if (value === null || typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') return sanitizeSensitiveText(value).slice(0, MAX_ERROR_MESSAGE_LENGTH);
  if (depth >= MAX_ERROR_DETAIL_DEPTH) return undefined;
  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ERROR_DETAIL_ARRAY_LENGTH)
      .map((item) => sanitizeDetailValue(item, depth + 1))
      .filter((item) => item !== undefined);
  }
  if (isRecord(value)) return sanitizeDetailRecord(value, depth);
  return undefined;
}

function sanitizeErrorText(value: unknown, fallback: string): string {
  if (typeof value !== 'string' || !value.trim()) return fallback;
  return sanitizeSensitiveText(value.trim()).slice(0, MAX_ERROR_MESSAGE_LENGTH) || fallback;
}

function sanitizeSensitiveText(value: string): string {
  return value
    .replace(/https?:\/\/[^\s)>\]}]+/giu, '[redacted-url]')
    .replace(
      /\b(access[_-]?token|refresh[_-]?token|authorization|bearer|api[_-]?key|secret)(\s*[:=]\s*|\s+)[^\s,;]+/giu,
      '$1=[redacted]',
    );
}

function isSensitiveErrorKey(key: string): boolean {
  const normalized = key.replace(/[^a-z0-9]/giu, '').toLowerCase();
  return ['token', 'secret', 'authorization', 'credential', 'cookie', 'url', 'uri', 'href', 'link', 'apikey']
    .some((fragment) => normalized.includes(fragment));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
